import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SMS_CONSENT_TEXT } from "@/lib/smsConsentText";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Pre-filled phone from profile (E.164 or raw). May be null. */
  initialPhone: string | null;
  /** Called after a consent row is logged. Receives the row id and the phone the user consented with. */
  onConsented: (consentId: string, phoneE164: string) => void;
  /** Called when the user closes the modal without consenting (cancel / X). */
  onCancel?: () => void;
}

function normalizeUsPhone(raw: string): string | null {
  const digits = (raw ?? "").replace(/\D+/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

function formatUsPhone(e164OrRaw: string | null): string {
  if (!e164OrRaw) return "";
  const d = e164OrRaw.replace(/\D+/g, "");
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  if (ten.length !== 10) return e164OrRaw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export function SMSConsentModal({ open, onOpenChange, initialPhone, onConsented, onCancel }: Props) {
  const initialFormatted = useMemo(() => formatUsPhone(initialPhone), [initialPhone]);
  const [phone, setPhone] = useState(initialFormatted);
  const [locked, setLocked] = useState<boolean>(!!initialPhone);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [consented, setConsented] = useState(false);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setPhone(initialFormatted);
      setLocked(!!initialPhone);
      setAgreed(false);
      setConsented(false);
      setSubmitting(false);
    }
  }, [open, initialFormatted, initialPhone]);

  const e164 = normalizeUsPhone(phone);
  const canSubmit = !!e164 && agreed && !submitting;

  const handleSubmit = async () => {
    if (!e164) {
      toast.error("Enter a valid US mobile number");
      return;
    }
    setSubmitting(true);
    try {
      const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : null;
      const { data, error } = await supabase.rpc("log_sms_consent", {
        p_phone: e164,
        p_consent_text: SMS_CONSENT_TEXT,
        p_consent_method: "in_app_settings",
        p_user_agent: userAgent,
      });
      if (error) throw error;
      const consentId = (data as unknown as string) ?? "";
      if (!consentId) throw new Error("No consent id returned");
      setConsented(true);
      onConsented(consentId, e164);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to record consent");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v: boolean) => {
    if (!v && !consented) onCancel?.();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Enable SMS notifications</DialogTitle>
          <DialogDescription>
            Review and confirm before we text this number. We'll send a verification code next.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="sms-consent-phone">Mobile number (US)</Label>
              {locked && (
                <button
                  type="button"
                  className="text-xs text-brand-teal hover:underline"
                  onClick={() => setLocked(false)}
                >
                  Change phone
                </button>
              )}
            </div>
            <Input
              id="sms-consent-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              readOnly={locked}
              inputMode="tel"
              className={locked ? "bg-muted text-muted-foreground" : ""}
            />
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm leading-relaxed text-foreground">
            {SMS_CONSENT_TEXT}
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              className="mt-0.5"
            />
            <span className="text-sm">
              I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-teal hover:underline"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-teal hover:underline"
              >
                Privacy Policy
              </a>{" "}
              and consent to receive the SMS messages described above.
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? "Saving..." : "Agree & continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
